import React, { useEffect, useState } from 'react';
import api from '../../services/api';

/**
 * Modal para configurar la numeración correlativa del Recibo / Detalle de Compra.
 * Cada sucursal tiene su propio talonario de papel; se configura cada uno por separado.
 */
const ModalConfiguracionRecibo = ({ onClose, onGuardado = () => {} }) => {
    const [proximoNumero, setProximoNumero] = useState(336);
    const [longitudDigitos, setLongitudDigitos] = useState(7);
    const [prefijo, setPrefijo] = useState('');
    const [talonario, setTalonario] = useState(null);
    const [talonarios, setTalonarios] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState(null);

    // Sin sucursal elegida, el servidor devuelve el talonario de la sucursal del usuario.
    const cargarTalonario = (sucursal) => {
        setCargando(true);
        setMensaje(null);
        api.get('/api/facturacion/recibos/configuracion/', { params: sucursal ? { sucursal } : {} })
            .then(r => {
                const d = r.data || {};
                setTalonario(d.sucursal);
                setTalonarios(d.talonarios || []);
                setProximoNumero(d.proximo_numero !== undefined ? d.proximo_numero : 336);
                setLongitudDigitos(d.longitud_digitos !== undefined ? d.longitud_digitos : 7);
                setPrefijo(d.prefijo || '');
            })
            .catch(err => {
                console.error('Error cargando configuración de recibo:', err);
            })
            .finally(() => setCargando(false));
    };

    useEffect(() => { cargarTalonario(null); }, []);

    // Vista previa formateada
    const numInt = Math.max(1, parseInt(proximoNumero, 10) || 1);
    const digitos = Math.max(3, Math.min(10, parseInt(longitudDigitos, 10) || 7));
    const vistaPrevia = `${prefijo}${String(numInt).padStart(digitos, '0')}`;

    const guardar = async (e) => {
        e.preventDefault();
        setGuardando(true);
        setMensaje(null);
        try {
            const { data } = await api.post('/api/facturacion/recibos/configuracion/', {
                sucursal: talonario,
                proximo_numero: numInt,
                longitud_digitos: digitos,
                prefijo: prefijo.trim()
            });
            setMensaje({ ok: true, texto: data.detail || 'Numeración guardada con éxito.' });
            onGuardado(data.configuracion);
            setTimeout(() => {
                onClose();
            }, 1200);
        } catch (err) {
            setMensaje({ ok: false, texto: err.response?.data?.detail || 'No se pudo guardar la configuración.' });
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            zIndex: 10050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16
        }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{
                background: '#ffffff',
                borderRadius: 16,
                width: 480,
                maxWidth: '100%',
                overflow: 'hidden',
                boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
            }}>
                <div style={{
                    background: '#164e9e',
                    color: '#ffffff',
                    padding: '16px 22px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <strong style={{ fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="bi bi-gear-fill"></i>
                        Configurar Numeración de Talonario
                    </strong>
                    <button onClick={onClose} style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                        fontSize: '1.6rem',
                        cursor: 'pointer',
                        lineHeight: 1
                    }}>&times;</button>
                </div>

                <form onSubmit={guardar} style={{ padding: '20px 24px' }}>
                    <p style={{ fontSize: '0.88rem', color: '#64748b', marginTop: 0, marginBottom: 16 }}>
                        Cada sucursal tiene su propio talonario. Configure el siguiente número para que coincida con el talonario físico de esa sucursal.
                    </p>

                    <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                            Talonario de la sucursal:
                        </label>
                        <select
                            value={talonario || ''}
                            onChange={e => cargarTalonario(e.target.value)}
                            disabled={cargando || guardando}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1.5px solid #cbd5e1',
                                borderRadius: 8,
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                boxSizing: 'border-box',
                                background: '#fff'
                            }}
                        >
                            {talonarios.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    {mensaje && (
                        <div style={{
                            padding: '10px 14px',
                            borderRadius: 8,
                            fontSize: '0.88rem',
                            fontWeight: 600,
                            marginBottom: 16,
                            background: mensaje.ok ? '#ecfdf5' : '#fef2f2',
                            color: mensaje.ok ? '#065f46' : '#991b1b',
                            border: `1px solid ${mensaje.ok ? '#a7f3d0' : '#fecaca'}`
                        }}>
                            {mensaje.texto}
                        </div>
                    )}

                    {/* VISTA PREVIA DEL NÚMERO */}
                    <div style={{
                        background: '#f8fafc',
                        border: '2px dashed #164e9e',
                        borderRadius: 10,
                        padding: '14px 16px',
                        textAlign: 'center',
                        marginBottom: 20
                    }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Próximo Recibo a Emitirse{talonario ? ` · ${talonario}` : ''}
                        </div>
                        <div style={{
                            fontSize: '2rem',
                            fontWeight: 900,
                            color: '#e11d48',
                            fontFamily: 'Courier New, Courier, monospace',
                            letterSpacing: '2px',
                            marginTop: 4
                        }}>
                            {vistaPrevia}
                        </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                            Siguiente número secuencial (del talonario):
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="99999999"
                            required
                            disabled={cargando || guardando}
                            value={proximoNumero}
                            onChange={e => setProximoNumero(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1.5px solid #cbd5e1',
                                borderRadius: 8,
                                fontSize: '1rem',
                                fontWeight: 700,
                                boxSizing: 'border-box'
                            }}
                            placeholder="Ej: 336"
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                                Dígitos totales (con ceros):
                            </label>
                            <input
                                type="number"
                                min="3"
                                max="10"
                                required
                                disabled={cargando || guardando}
                                value={longitudDigitos}
                                onChange={e => setLongitudDigitos(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    fontSize: '0.92rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                                Prefijo (opcional):
                            </label>
                            <input
                                type="text"
                                disabled={cargando || guardando}
                                value={prefijo}
                                onChange={e => setPrefijo(e.target.value)}
                                placeholder="Ej: R- o vacío"
                                style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    fontSize: '0.92rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={guardando}
                            style={{
                                padding: '10px 16px',
                                borderRadius: 8,
                                border: '1.5px solid #cbd5e1',
                                background: '#fff',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Cancelar
                        </button>

                        <button
                            type="submit"
                            disabled={guardando || cargando}
                            style={{
                                padding: '10px 20px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#164e9e',
                                color: '#fff',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                boxShadow: '0 2px 6px rgba(22, 78, 158, 0.3)'
                            }}
                        >
                            <i className="bi bi-check-lg"></i>
                            {guardando ? 'Guardando...' : 'Guardar Numeración'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ModalConfiguracionRecibo;
